import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Eye, RefreshCw } from "lucide-react";

import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { Notice, NoticeText } from "components/ui/Notice";
import { useReplayDeadLetter } from "features/healthlink/hooks/useReplayDeadLetter";

import styles from "../../HealthlinkDetailsPage.module.css";
import { formatDate } from "../../utils/formatDate";

/**
 * The raw message, and nothing else.
 *
 * `xml` is unparsed HL7 carrying patient identifiers and clinical content, so it exists here
 * only as rendered text: never logged, never stored, never in a URL, never in an analytics or
 * error-tracker payload. Copying is a deliberate user action, which is different from the
 * application moving it somewhere on its own.
 */
const DeadLetterDialog = ({ deadLetter, onClose }) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(deadLetter.xml ?? "");
      toast.success("Message copied.");
    } catch (error) {
      // Clipboard access is refused in some browsers and over plain HTTP. The text is on
      // screen and selectable either way, so say that rather than failing silently.
      console.error("Clipboard write failed");
      toast.error("Could not copy. Select the text and copy it manually.");
    }
  };

  return (
    <Dialog
      open={!!deadLetter}
      onClose={onClose}
      title="Unparsed message"
      width={900}
    >
      <div className={styles.deadLetterDialogBody}>
        <div className={styles.deadLetterMeta}>
          <div>
            <span className={styles.deadLetterMetaLabel}>Received</span>
            <span>{formatDate(deadLetter.received_at)}</span>
          </div>
          <div>
            <span className={styles.deadLetterMetaLabel}>Type</span>
            <span>{deadLetter.messageType || "-"}</span>
          </div>
        </div>

        <div>
          <span className={styles.deadLetterMetaLabel}>Why it failed</span>
          <p className={styles.deadLetterReason}>
            {deadLetter.parseError || "No reason was recorded."}
          </p>
        </div>

        <div className={styles.deadLetterXmlHeader}>
          <span className={styles.deadLetterMetaLabel}>Raw message</span>
          <Button variant="secondary" onClick={handleCopy}>
            Copy
          </Button>
        </div>
        <pre className={styles.deadLetterXml}>{deadLetter.xml}</pre>
      </div>
    </Dialog>
  );
};

export const DeadLettersTab = ({
  data,
  total,
  isLoading,
  currentPage,
  rowsPerPage,
  onPageChange,
  onRowsChange,
}) => {
  const [selected, setSelected] = useState(null);
  const { mutate: replay, isPending: isReplaying } = useReplayDeadLetter();

  const columns = useMemo(
    () => [
      {
        id: "received_at",
        header: "Received",
        width: "minmax(150px, 1fr)",
        render: (row) => formatDate(row.received_at),
      },
      {
        id: "messageType",
        header: "Type",
        width: "minmax(120px, 1fr)",
        // The XML element name, not a friendly label — ORU_R01, VXU_V04, ACK. Left as sent:
        // this view exists for whoever is diagnosing the message, and the element name is
        // what they will search the spec for.
        render: (row) => row.messageType || "-",
      },
      {
        id: "parseError",
        header: "Why it failed",
        width: "minmax(240px, 3fr)",
        getTooltipText: (row) => row.parseError || "-",
        render: (row) => row.parseError || "-",
      },
    ],
    []
  );

  const rowActions = useMemo(
    () => [
      {
        label: "View message",
        icon: Eye,
        onClick: (row) => setSelected(row),
      },
      {
        label: "Try again",
        icon: RefreshCw,
        onClick: (row) => replay(row.id),
        disabled: isReplaying,
      },
    ],
    [replay, isReplaying]
  );

  return (
    <div className={styles.content}>
      <Notice tone="info" testId="dead-letters-notice">
        <NoticeText>
          Inbound messages Healthlink sent that could not be read. They are held
          here instead of being retried for ever, and are not filed against any
          patient. Once the gap is fixed, try one again.
        </NoticeText>
      </Notice>

      <DataTable
        columns={columns}
        data={data}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        loadingTitle="Loading unparsed messages..."
        loadingText="Please wait."
        emptyTitle="Nothing unread"
        emptyText="Every inbound Healthlink message has been understood. Nothing needs attention here."
        currentPage={currentPage}
        totalCount={total}
        rowsValue={rowsPerPage}
        onPageChange={onPageChange}
        onRowsChange={onRowsChange}
        rowActions={rowActions}
        rowActionsLabel="Message actions"
      />

      {!!selected && (
        <DeadLetterDialog
          deadLetter={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};
